import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useTheme } from '../../context/ThemeContext';
import { getTheme } from '../../theme';
import MainLayout from '../../components/Layout/MainLayout';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { api } from '../../services/api';
import CustomerLogsAnalytics from '../../components/Customers/CustomerLogsAnalytics';
import CustomerActivityTimeline from '../../components/Customers/CustomerActivityTimeline';
import CustomerMealPlan from '../../components/Customers/CustomerMealPlan';
import { 
  ArrowBack, 
  Person, 
  Email, 
  Phone, 
  Scale, 
  Height, 
  CalendarToday, 
  FitnessCenter,
  Edit,
  Delete,
  Notifications
} from '@mui/icons-material';
import PushNotificationModal from '../../components/Customers/PushNotificationModal';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin: 0;
  flex: 1;
  ${props => props.theme?.isDark && `
    text-shadow: 0 0 10px rgba(25, 184, 136, 0.3);
  `}
`;

const Actions = styled.div`
  display: flex;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
`;

const ProfileCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: ${props => getTheme(props.theme?.isDark).spacing.xl};
`;

const AvatarContainer = styled.div`
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const Avatar = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid ${props => getTheme(props.theme?.isDark).colors.primary};
`;

const AvatarPlaceholder = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: ${props => getTheme(props.theme?.isDark).colors.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 48px;
  font-weight: 700;
  border: 3px solid ${props => getTheme(props.theme?.isDark).colors.primary};
`;

const CustomerName = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  margin: ${props => getTheme(props.theme?.isDark).spacing.sm} 0;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: ${props => getTheme(props.theme?.isDark).spacing.xs} ${props => getTheme(props.theme?.isDark).spacing.md};
  border-radius: ${props => getTheme(props.theme?.isDark).borderRadius.md};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  background: ${props => {
    if (props.status === 'active') return getTheme(props.theme?.isDark).colors.success || '#19B888';
    if (props.status === 'inactive') return getTheme(props.theme?.isDark).colors.error || '#F44336';
    return getTheme(props.theme?.isDark).colors.border;
  }};
  color: white;
`;

const DetailCard = styled(Card)`
  padding: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const ProfileRight = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${props => getTheme(props.theme?.isDark).spacing.md};
`;

const DetailItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${props => getTheme(props.theme?.isDark).spacing.sm};
`;

const DetailIcon = styled.div`
  color: ${props => getTheme(props.theme?.isDark).colors.primary};
  display: flex;
  align-items: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const DetailContent = styled.div`
  flex: 1;
`;

const DetailLabel = styled.div`
  font-size: 12px;
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${props => getTheme(props.theme?.isDark).spacing.xs};
`;

const DetailValue = styled.div`
  font-size: 14px;
  color: ${props => getTheme(props.theme?.isDark).colors.text};
  font-weight: 500;
`;

const LogsAndActivitiesContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 400px;
  gap: ${props => getTheme(props.theme?.isDark).spacing.lg};
  align-items: start;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => getTheme(props.theme?.isDark).spacing.lg};
`;

const CustomerDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const theme = { isDark };
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);

  useEffect(() => {
    fetchCustomer();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchCustomer = async () => {
    try {
      setLoading(true);
      const response = await api.getCustomer(id);
      setCustomer(response);
    } catch (error) {
      console.error('Error fetching customer:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this customer?')) {
      try {
        await api.deleteCustomer(id);
        navigate('/admin/customers');
      } catch (error) {
        console.error('Error deleting customer:', error);
      }
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const getAvatarUrl = () => {
    if (customer?.avatarPath) return customer.avatarPath;
    if (customer?.avatar) {
      const baseUrl = (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development') 
        ? 'http://127.0.0.1:3000' 
        : 'https://api.application.fit';
      return `${baseUrl}${customer.avatar}`;
    }
    return null;
  };

  if (loading) {
    return (
      <MainLayout>
        <PageContainer theme={theme}>
          <LoadingContainer theme={theme}>Loading customer details...</LoadingContainer>
        </PageContainer>
      </MainLayout>
    );
  }

  if (!customer) {
    return (
      <MainLayout>
        <PageContainer theme={theme}>
          <EmptyState theme={theme}>Customer not found</EmptyState>
        </PageContainer>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <PageContainer theme={theme}>
        <Header>
          <BackButton onClick={() => navigate('/admin/customers')} theme={theme} variant="outline">
            <ArrowBack /> Back
          </BackButton>
          <Title theme={theme}>Customer Details</Title>
          <Actions>
            <Button onClick={() => setIsNotificationModalOpen(true)} theme={theme}>
              <Notifications /> Push Notification
            </Button>
            <Button onClick={() => navigate(`/admin/customers/${id}/edit`)} theme={theme} variant="outline">
              <Edit /> Edit
            </Button>
            <Button onClick={handleDelete} theme={theme} variant="danger">
              <Delete /> Delete
            </Button>
          </Actions>
        </Header>

        <ProfileCard theme={theme}>
          <AvatarContainer theme={theme}>
            {getAvatarUrl() ? (
              <Avatar src={getAvatarUrl()} alt={customer.name || 'Customer'} />
            ) : (
              <AvatarPlaceholder theme={theme}>
                {getInitials(customer.name)}
              </AvatarPlaceholder>
            )}
          </AvatarContainer>
          <CustomerName theme={theme}>{customer.name || 'No Name'}</CustomerName>
          {customer.status && (
            <StatusBadge theme={theme} status={customer.status}>
              {customer.status}
            </StatusBadge>
          )}
        </ProfileCard>

        {/* Log Analizi və Aktivliklər */}
        <LogsAndActivitiesContainer theme={theme}>
          <LeftColumn theme={theme}>
            <DetailCard theme={theme}>
              <ProfileRight theme={theme}>
                {customer.phone && (
                  <DetailItem theme={theme}>
                    <DetailIcon theme={theme}>
                      <Phone />
                    </DetailIcon>
                    <DetailContent>
                      <DetailLabel theme={theme}>Phone</DetailLabel>
                      <DetailValue theme={theme}>{customer.phone}</DetailValue>
                    </DetailContent>
                  </DetailItem>
                )}
                {customer.email && (
                  <DetailItem theme={theme}>
                    <DetailIcon theme={theme}>
                      <Email />
                    </DetailIcon>
                    <DetailContent>
                      <DetailLabel theme={theme}>Email</DetailLabel>
                      <DetailValue theme={theme}>{customer.email}</DetailValue>
                    </DetailContent>
                  </DetailItem>
                )}
                {customer.gender && (
                  <DetailItem theme={theme}>
                    <DetailIcon theme={theme}>
                      <Person />
                    </DetailIcon>
                    <DetailContent>
                      <DetailLabel theme={theme}>Gender</DetailLabel>
                      <DetailValue theme={theme}>{customer.gender}</DetailValue>
                    </DetailContent>
                  </DetailItem>
                )}
                {customer.birthdate && (
                  <DetailItem theme={theme}>
                    <DetailIcon theme={theme}>
                      <CalendarToday />
                    </DetailIcon>
                    <DetailContent>
                      <DetailLabel theme={theme}>Birthdate</DetailLabel>
                      <DetailValue theme={theme}>
                        {new Date(customer.birthdate).toLocaleDateString()} 
                        ({new Date().getFullYear() - new Date(customer.birthdate).getFullYear()} years old)
                      </DetailValue>
                    </DetailContent>
                  </DetailItem>
                )}
                {customer.height && (
                  <DetailItem theme={theme}>
                    <DetailIcon theme={theme}>
                      <Height />
                    </DetailIcon>
                    <DetailContent>
                      <DetailLabel theme={theme}>Height</DetailLabel>
                      <DetailValue theme={theme}>{customer.height} cm</DetailValue>
                    </DetailContent>
                  </DetailItem>
                )}
                {customer.weight && (
                  <DetailItem theme={theme}>
                    <DetailIcon theme={theme}>
                      <Scale />
                    </DetailIcon>
                    <DetailContent>
                      <DetailLabel theme={theme}>Initial Weight</DetailLabel>
                      <DetailValue theme={theme}>{customer.weight} kg</DetailValue>
                    </DetailContent>
                  </DetailItem>
                )}
                {customer.currentWeight && (
                  <DetailItem theme={theme}>
                    <DetailIcon theme={theme}>
                      <Scale />
                    </DetailIcon>
                    <DetailContent>
                      <DetailLabel theme={theme}>Current Weight</DetailLabel>
                      <DetailValue theme={theme}>{customer.currentWeight} kg</DetailValue>
                    </DetailContent>
                  </DetailItem>
                )}
                {customer.weightTo && (
                  <DetailItem theme={theme}>
                    <DetailIcon theme={theme}>
                      <Scale />
                    </DetailIcon>
                    <DetailContent>
                      <DetailLabel theme={theme}>Target Weight</DetailLabel>
                      <DetailValue theme={theme}>{customer.weightTo} kg</DetailValue>
                    </DetailContent>
                  </DetailItem>
                )}
                {customer.bmi && (
                  <DetailItem theme={theme}>
                    <DetailIcon theme={theme}>
                      <FitnessCenter />
                    </DetailIcon>
                    <DetailContent>
                      <DetailLabel theme={theme}>BMI</DetailLabel>
                      <DetailValue theme={theme}>{customer.bmi.toFixed(1)}</DetailValue>
                    </DetailContent>
                  </DetailItem>
                )}
                {customer.createdAt && (
                  <DetailItem theme={theme}>
                    <DetailContent>
                      <DetailLabel theme={theme}>Created At</DetailLabel>
                      <DetailValue theme={theme}>
                        {new Date(customer.createdAt).toLocaleString()}
                      </DetailValue>
                    </DetailContent>
                  </DetailItem>
                )}
              </ProfileRight>
            </DetailCard>
            {customer.logs && (
              <CustomerLogsAnalytics logs={customer.logs} customer={customer} />
            )}
            {customer.mealPlan && (
              <CustomerMealPlan mealPlan={customer.mealPlan} />
            )}
          </LeftColumn>
          <CustomerActivityTimeline customerId={id} />
        </LogsAndActivitiesContainer>

        <PushNotificationModal
          isOpen={isNotificationModalOpen}
          onClose={() => setIsNotificationModalOpen(false)}
          customerId={id}
          onSuccess={() => {
            // Optionally refresh customer data
            fetchCustomer();
          }}
        />
      </PageContainer>
    </MainLayout>
  );
};

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${props => getTheme(props.theme?.isDark).spacing.xxl};
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => getTheme(props.theme?.isDark).spacing.xxl};
  color: ${props => getTheme(props.theme?.isDark).colors.textSecondary};
`;

export default CustomerDetails;
